import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';

declare global {
  interface Window {
    SwaggerUIBundle?: {
      (options: Record<string, unknown>): unknown;
      presets: {
        apis: unknown;
      };
    };
    SwaggerUIStandalonePreset?: unknown;
    ui?: unknown;
  }
}

export function SwaggerPage() {
  const { user, isLoading, signIn } = useAuth();
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isLoading && !user) {
      signIn('/swagger');
    }
  }, [isLoading, user, signIn]);

  useEffect(() => {
    if (!user) return;

    const cssId = 'swagger-ui-css';
    if (!document.getElementById(cssId)) {
      const link = document.createElement('link');
      link.id = cssId;
      link.rel = 'stylesheet';
      link.href = '/swagger/swagger-ui.css';
      document.head.appendChild(link);
    }

    const loadScript = (src: string): Promise<void> => {
      return new Promise((resolve, reject) => {
        const existing = document.querySelector(`script[src="${src}"]`);
        if (existing) {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = reject;
        document.body.appendChild(script);
      });
    };

    let cancelled = false;
    Promise.all([
      loadScript('/swagger/swagger-ui-bundle.js'),
      loadScript('/swagger/swagger-ui-standalone-preset.js')
    ])
      .then(() => {
        if (!cancelled) setScriptsLoaded(true);
      })
      .catch((err: unknown) => {
        console.error('Failed to load Swagger UI scripts:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (scriptsLoaded && window.SwaggerUIBundle && containerRef.current) {
      window.ui = window.SwaggerUIBundle({
        url: '/api-docs',
        dom_id: '#swagger-ui-container',
        deepLinking: true,
        persistAuthorization: true,
        displayRequestDuration: true,
        requestInterceptor: (req: Record<string, unknown>) => {
          const url = typeof req.url === 'string' ? req.url : '';
          // Allow OpenAPI docs and Keycloak token exchange requests through
          if (
            url.includes('/api-docs') ||
            url.includes('/protocol/openid-connect/token')
          ) {
            return req;
          }

          // Mandate Authorization header for all API calls executed in Swagger UI
          const headers = (req.headers as Record<string, unknown> | undefined) || {};
          const auth = headers['Authorization'] || headers['authorization'];
          if (!auth || typeof auth !== 'string' || !auth.trim()) {
            throw new Error(
              'Authorization required: You must click the "Authorize" button at the top and enter your Keycloak Client ID and Client Secret before executing any API request.'
            );
          }
          return req;
        },
        presets: [
          window.SwaggerUIBundle.presets.apis,
          window.SwaggerUIStandalonePreset
        ],
        layout: 'StandaloneLayout'
      });
    }
  }, [scriptsLoaded]);

  if (isLoading || !user) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          color: '#64748b',
          fontSize: '16px'
        }}
      >
        <p>Verifying PeruseLab session...</p>
      </div>
    );
  }

  const displayName =
    (user.profile.name as string | undefined) ||
    (user.profile.preferred_username as string | undefined) ||
    (user.profile.email as string | undefined) ||
    'user';

  return (
    <div style={{ background: '#fafafa', minHeight: '100vh' }}>
      <div
        style={{
          background: '#1e293b',
          color: '#f8fafc',
          padding: '12px 24px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontSize: '14px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <div>
          <span>
            Authenticated as <strong>{displayName}</strong>. To execute API requests, click <strong>Authorize</strong> and enter your <strong>Client ID &amp; Secret</strong>.
          </span>
        </div>
        <div>
          <Link
            to="/"
            style={{
              color: '#38bdf8',
              textDecoration: 'none',
              fontWeight: 500
            }}
          >
            ← Return to App
          </Link>
        </div>
      </div>
      <div id="swagger-ui-container" ref={containerRef} />
    </div>
  );
}
